import React, { Dispatch, useMemo, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { get, isEmpty, forEach, map, findKey } from "lodash"

import { Panel } from "components/organisms/panel"
import { BaseTable, SeparateTable } from "components/molecules/table"
import { tableColumns } from "config/table_columns"
import { BaseSelect } from "components/atoms/input/select"
import MiddleChart from "components/molecules/chart/middleChart"
import { NewsList } from "components/organisms/panel/newsList"
import { Labels } from "components/atoms/input/input"
import {
  usePriceListAction,
  usePriceListState,
} from "components/actions/priceList"
import { APIResponse } from "redux/apis/types"
import {
  changeSymbol,
  changeTerm,
  changeSpModalSymbol,
} from "redux/ducks/Chart"
import uuid from "uuid"
import { MiniChartListLegFilter } from "components/atoms/chart/parts/ChartLegFilter"
import { TERM } from "components/atoms/chart/logic/chartConfig"
import { MiniChartComponent } from "components/molecules/chart/miniChart"
import { checkIsSp } from "utils/util"
import ClassNames from "classnames"
import { ROUTE_LIST } from "config/const"
import { MainContainer } from "components/molecules/utils/mainContainer"
import { useLodingTrigger } from "components/actions/loading"
import { DataDescription } from "../../organisms/panel/dataDescription"
import { FXDisclaimerText } from "config/disclaimerText"

type LabelList = {
  name: string
  id: string | number
}

type ForeignTemplateType = {
  currencyList: APIResponse.Res
  currencyListState: usePriceListState
  currencyListAction: Dispatch<usePriceListAction>
  totalCount: number
  transitionData: object
  setRefreshFlag: Function
}

const label: Array<LabelList> = [
  { name: "一覧", id: 1 },
  { name: "ミニチャート", id: 2 },
]

const countryLabel: Array<LabelList> = [
  { name: "日本", id: "Top.FX.JPY" },
  { name: "米国", id: "Top.FX.USD" },
]

export const ForeignTemplate: React.FC<ForeignTemplateType> = props => {
  const [currentPosition, changePosition] = useState<number>(0)
  const [displayList, changeDisplay] = useState({ type: 1 })
  const [legValue, setLegValue] = useState(TERM[8].id)
  const [screenSize, setScreenSize] = useState(window.outerWidth)
  const [checkTransitionData, setCheckTransitionData] = useState(false)
  const [newsEffect, setNewsEffect] = useState(false)
  const isPhone = checkIsSp()
  const { loadingTrigger } = useLodingTrigger()

  const miniChartId = useMemo(() => {
    if (!props.totalCount) return
    const idList: Array<string> = []
    for (let i = 0; i < props.totalCount; i++) {
      idList.push(uuid())
    }
    return idList
  }, [props.totalCount])

  const canvasId = useMemo(() => {
    return uuid()
  }, [])

  const dispatch = useDispatch()
  const changeList: Function = (id: number) => {
    changeDisplay({ type: id })
  }

  const changeChartLeg = (id: string) => {
    forEach(miniChartId, items => {
      dispatch(changeTerm(items, Number(id)))
    })
    setLegValue(Number(id))
  }

  useEffect(() => {
    const data = get(props.currencyList, "ResultList")
    if (isEmpty(data)) return
    if (props.transitionData && !checkTransitionData) {
      const activePosition = findKey(data, (row: object) => {
        const symbol = get(props.transitionData, "Symbol")
        return get(row, "Symbol") === symbol
      })
      changePosition(Number(activePosition))
      dispatch(changeSymbol(canvasId, props.transitionData))
      setCheckTransitionData(true)
    } else {
      dispatch(changeSymbol(canvasId, data[0]))
    }
  }, [props.currencyList, props.transitionData, displayList])

  useEffect(() => {
    window.addEventListener("resize", () => {
      setScreenSize(window.innerWidth)
    })
  })

  return (
    <MainContainer className="top-foreign-template">
      <div className="symmetry-layout top-foreign">
        <div
          className={ClassNames("foreign-list-block", {
            "contents-block": displayList.type === label[0].id,
          })}
        >
          <div
            className={ClassNames("foreign-list-block", {
              "contents-nest-block": displayList.type === label[0].id,
            })}
          >
            <Panel
              className={ClassNames("panel-top-foreign", {
                "list-panel": displayList.type === label[0].id,
              })}
              title=""
              // refresh={() => {
              //   props.setRefreshFlag(true)
              //   setNewsEffect(true)
              // }}
              filter={
                <div className="control-row panel-header-filter">
                  {!isPhone && (
                    <Labels
                      className="btn-segmented-control btn-thin liquid-control filter-control-item"
                      labelList={label}
                      checked={displayList.type}
                      onChange={(id: number) => {
                        changeList(id)
                        if (displayList.type === label[1].id) {
                          loadingTrigger.setLoading(true)
                          const data = get(props.currencyList, "ResultList")
                          changePosition(0)
                          dispatch(changeSymbol(canvasId, data[0]))
                        }
                      }}
                    />
                  )}
                  <BaseSelect
                    option={countryLabel}
                    value={props.currencyListState.name}
                    onChange={(id: string) => {
                      changePosition(0)
                      setNewsEffect(true)
                      return props.currencyListAction({
                        type: "PRICE_LIST_CHANGE_TYPE",
                        newType: id,
                      })
                    }}
                  />
                  {displayList.type === label[1].id ? (
                    <MiniChartListLegFilter
                      onChange={changeChartLeg}
                      defaltValueId={legValue}
                    />
                  ) : null}
                </div>
              }
            >
              <div className="summary-chart-panel">
                {displayList.type === label[0].id ? (
                  <SeparateTable
                    className="table-list-view table-liquid-cell"
                    list={tableColumns.index.currencySummary}
                    data={get(props.currencyList, "ResultList")}
                    active={currentPosition}
                    onClick={(data: any, i: number) => {
                      isPhone
                        ? dispatch(changeSpModalSymbol(data))
                        : dispatch(changeSymbol(canvasId, data))
                      changePosition(i)
                    }}
                    scrollable={screenSize > 559}
                  />
                ) : (
                  <ul className="chart-list-contents chart-list thin">
                    {map(
                      get(props.currencyList, "ResultList"),
                      (row, key: number) => {
                        dispatch(changeSymbol(miniChartId[key], row))
                        return get(row, "ChartFlg") ? (
                          <MiniChartComponent
                            key={key}
                            data={row}
                            id={miniChartId[key]}
                            lineType={true}
                          />
                        ) : null
                      }
                    )}
                  </ul>
                )}
              </div>
            </Panel>
          </div>
        </div>
        {displayList.type === label[0].id && (
          <div className="contents-block">
            <div className="contents-nest-block">
              {!isPhone && (
                <Panel
                  className="panel-main-index"
                  title=""
                  nonePanelHeader={true}
                >
                  <MiddleChart id={canvasId} />
                </Panel>
              )}
              <NewsList
                route={ROUTE_LIST.TOP_EXCHANGE}
                effect={newsEffect}
                setEffect={setNewsEffect}
              />
            </div>
          </div>
        )}
      </div>
      {!isPhone && (
        <DataDescription
          className="panel-data-description"
          children={FXDisclaimerText}
        />
      )}
    </MainContainer>
  )
}
